import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { buildGraphConfig, buildLegendLabels } from '../../../../utils/reportUtils';
import LineChart from './LineChart';
import { useMediaQuery, } from "react-responsive";

const PAGE_SIZE = 4;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const parseReportedDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr.includes('T')) return new Date(dateStr);
  
  const [dayStr, month, yearStr] = dateStr.split(' ');
  if (!dayStr || !month || !yearStr) return new Date();
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = monthNames.indexOf(month);
  
  return new Date(parseInt(yearStr), monthIdx, parseInt(dayStr));
};

const formatMonthYear = (date) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const year = date.getFullYear().toString().slice(-2);
  return `${monthNames[date.getMonth()]} ${year}`;
};

const formatMonthYearFull = (date) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

const getDaySuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatISODate = (isoString) => {
  const date = new Date(isoString);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  
  return `${day}${getDaySuffix(day)} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Generate last 12 months from TODAY (backwards)
 * Used for "Last 12 months" tab view
 */
const generateLast12MonthsFromToday = () => {
  const today = new Date();
  const months = [];
  
  // Generate 12 months going back from today
  for (let i = 11; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    months.push({
      label: formatMonthYear(date),
      date: new Date(date),
      value: null,
      fullDate: null,
    });
  }
  
  return months;
};

const processPointsInMonth = (points, monthLabel = '') => {
  if (points.length <= 1) {
    return points;
  }
  
  const dateGroups = {};
  points.forEach(p => {
    const date = parseReportedDate(p.fullDate);
    const dateKey = date.toDateString(); 
    
    if (!dateGroups[dateKey]) {
      dateGroups[dateKey] = [];
    }
    dateGroups[dateKey].push(p);
  });
  
  const kept = [];
  Object.entries(dateGroups).forEach(([dateKey, datePoints]) => {
    const sorted = [...datePoints].sort((a, b) => {
      const timeA = parseReportedDate(a.fullDate).getTime();
      const timeB = parseReportedDate(b.fullDate).getTime();
      return timeB - timeA;
    });
    
    const latest = sorted[0];
    kept.push(latest);
    
  });
  
  return kept;
};


// ----------------------tooltip component----------------------
function Tooltip({ data, index, unit, getPointCategory, position, visible }) {
  if (!visible || index == null || !data[index] || data[index].value === null) return null;

  const d = data[index];
  const cat = getPointCategory(d.value);
  const displayDate = d.fullDate ? formatISODate(d.fullDate) : (d.label || '');

  const tooltipStyle = {
    position: 'fixed',
    left: position?.clientX || 0,
    top: (position?.clientY || 0) - 10,
    transform: 'translateX(-50%) translateY(-100%)',
    zIndex: 1000,
    pointerEvents: 'none'
  };

  return (
    <div className="mg__tooltip-wrap" style={tooltipStyle}>
      <div className="mg__tooltip" style={{ border: `1.5px solid ${cat.zoneFill}` }}>
        <div className="mg__tooltip-value">{d.value} {unit}</div>
        <div className="mg__tooltip-label">{displayDate}</div>
        {d.sample_type && <div className="mg___tooltip-sample">{d.sample_type}</div>}
        <span className="mg__tooltip-badge" style={{ background: cat.badgeBg, color: cat.textColor }}>
          {d.decision_type?.includes("manual") ? d.result : cat.label}
        </span>
      </div>
    </div>
  );
}


export default function MarkerGraph({
  marker = marker,
  data = [],
  reportedDate = '2026-01-15T12:34:55.796571Z',
  periodLabel: propPeriodLabel = '',
  historicalData,
  isMobileView
}) {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  
  const transformedHistoricalData = useMemo(() => {
    if (!Array.isArray(historicalData) || historicalData.length === 0) {
      return [];
    }

    return historicalData
      .map((item, idx) => {
        try {
          const value = parseFloat(item.value);
          if (isNaN(value)) return null;

          const parsedDate = parseReportedDate(item.reported_date);
          const monthKey = `${parsedDate.getFullYear()}-${parsedDate.getMonth()}`;

          return {
            value,
            fullDate: item.reported_date,
            label: formatMonthYear(parsedDate),
            sample_type: item.sample_type,
            unit: item.unit,
            result: item.result,
            originalIndex: idx,
            date: parsedDate,
            monthKey: monthKey,
          };
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [historicalData]);

  const aggregatedAllResultsData = useMemo(() => {
    return transformedHistoricalData.map((item, idx) => ({
      ...item,
      chronologicalIndex: idx,
    }));
  }, [transformedHistoricalData]);

  const historyData = useMemo(() => {
    if (transformedHistoricalData.length > 0) {
      return transformedHistoricalData;
    }

    if (Array.isArray(data) && data.length > 0) {
      return data.map((point, idx) => ({
        ...point,
        fullDate: point.fullDate || point.label,
        value: parseFloat(point.value) || null,
        label: point.label || formatMonthYear(parseReportedDate(point.fullDate || point.label)),
        sample_type: point.sample_type || '',
        originalIndex: idx,
        chronologicalIndex: idx,
      }));
    }

    const val = parseFloat(marker?.value);
    if (!isNaN(val)) {
      return [{
        label: formatMonthYear(parseReportedDate(reportedDate)) || 'Now',
        value: val,
        fullDate: reportedDate || 'Now',
        originalIndex: 0,
        chronologicalIndex: 0,
      }];
    }
    return [];
  }, [transformedHistoricalData, data, marker, reportedDate]);

  const { zoneBands, yTicks, ranges, yMax, getPointCategory, getValuePositionInZone } = buildGraphConfig(marker);
  const currentValue = parseFloat(marker.value) || 0;
  const unit = marker.units || historicalData[0]?.unit || '';

  const twelveMonthData = useMemo(() => {
  const twelveMonths = generateLast12MonthsFromToday();
  const result = [];

  twelveMonths.forEach((month) => {
    const matchingPoints = historyData.filter(point => {
      if (!point.fullDate) return false;

      const pointDate = parseReportedDate(point.fullDate);

      return (
        pointDate.getMonth() === month.date.getMonth() &&
        pointDate.getFullYear() === month.date.getFullYear()
      );
    });

    if (matchingPoints.length) {
      const processedPoints = processPointsInMonth(matchingPoints, month.label);
      processedPoints.forEach(point => {
        result.push({
          ...month,
          ...point,
        });
      });
    } else {
      result.push({
        ...month,
        value: null,
      });
    }
  });

  return result;
}, [historyData]);

  const [tab, setTab] = useState('all');
  const [windowStart, setWindowStart] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const calculatePointOffsets = (pointsInMonth, totalSlots = 4) => {
    if (pointsInMonth.length <= 1) {
      return pointsInMonth.map(() => 0.5); 
    }
    
    const startOffset = 0.2;
    const endOffset = 0.8;
    const step = (endOffset - startOffset) / (pointsInMonth.length - 1);
    
    return pointsInMonth.map((_, idx) => startOffset + (idx * step));
  };

  // Use aggregated data for "All Results" tab
  const currentData = useMemo(() => {
    const baseData = tab === 'last12' ? twelveMonthData : aggregatedAllResultsData;
    
    if (tab === 'all') {      
      const monthGroups = {};
      baseData.forEach(point => {
        const key = point.monthKey;
        if (!monthGroups[key]) monthGroups[key] = [];
        monthGroups[key].push(point);
      });
            
      const processed = [];
      Object.entries(monthGroups).forEach(([monthKey, monthPoints]) => {
        const deduplicated = processPointsInMonth(monthPoints, monthKey);

        deduplicated.forEach((point, idx) => {
          processed.push({
            ...point,
            monthKey: monthKey,
            pointIndexInMonth: idx, 
            totalPointsInMonth: deduplicated.length
          });
        });
      });
      
      const sorted = processed.sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
      return sorted;
    }
    
    return baseData;
  }, [tab, twelveMonthData, aggregatedAllResultsData]);

  const visibleData = useMemo(() => {
    const slice = currentData.slice(windowStart, windowStart + PAGE_SIZE);
    
    return slice.map((item, idx) => ({
      ...item,
      displayLabel: item.label,
      labelForAxis: item.label
    }));
  }, [currentData, windowStart]);

  const visibleLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    visibleData.forEach(item => {
      if (!seen.has(item.label)) {
        seen.add(item.label);
        labels.push(item.label);
      }
    });
    return labels;
  }, [visibleData]);
  const canGoPrev = windowStart > 0;
  const canGoNext = windowStart + PAGE_SIZE < currentData.length;

  const visibleIndices = useMemo(() => {
    return currentData.map((_, idx) => idx).slice(windowStart, windowStart + PAGE_SIZE);
  }, [currentData, windowStart]);

  const findCurrentMarkerIndex = useCallback(() => {
    const reportedDateObj = parseReportedDate(reportedDate);
    const reportedMonthKey = `${reportedDateObj.getFullYear()}-${reportedDateObj.getMonth()}`;
    
    const index = currentData.findIndex(item => item.monthKey === reportedMonthKey);
    return index !== -1 ? index : currentData.length - 1;
  }, [tab, currentData, reportedDate]);

  useEffect(() => {
    const currentIndex = findCurrentMarkerIndex();
    setActiveIdx(currentIndex);

    const newWindowStart = Math.max(0, Math.min(
      currentIndex - Math.floor(PAGE_SIZE / 2),
      currentData.length - PAGE_SIZE
    ));
    setWindowStart(Math.max(0, newWindowStart));
  }, [tab, currentData.length, findCurrentMarkerIndex]);

  const goPrev = () => {
    const next = Math.max(0, windowStart - PAGE_SIZE);
    setWindowStart(next);
    setActiveIdx(prev => clamp(prev, next, next + PAGE_SIZE - 1));
  };

  const goNext = () => {
    const next = Math.min(currentData.length - PAGE_SIZE, windowStart + PAGE_SIZE);
    setWindowStart(next);
    setActiveIdx(prev => clamp(prev, next, next + PAGE_SIZE - 1));
  };

  const dynamicPeriodLabel = useMemo(() => {
    if (propPeriodLabel) return propPeriodLabel;
    if (aggregatedAllResultsData.length === 0) return '';

    const firstMonth = aggregatedAllResultsData[0]?.date;
    const lastMonth = aggregatedAllResultsData[aggregatedAllResultsData.length - 1]?.date;

    if (firstMonth && lastMonth) {
      if (firstMonth === lastMonth) return formatMonthYearFull(firstMonth);
      return `${formatMonthYearFull(firstMonth)} - ${formatMonthYearFull(lastMonth)}`;
    }
    return '';
  }, [aggregatedAllResultsData, propPeriodLabel]);

  const twelveMonthPeriodLabel = useMemo(() => {
    if (twelveMonthData.length === 0) return '';

    const firstMonth = twelveMonthData[0]?.date;
    const lastMonth = twelveMonthData[twelveMonthData.length - 1]?.date;

    if (firstMonth && lastMonth) {
      return `${formatMonthYearFull(firstMonth)} - ${formatMonthYearFull(lastMonth)}`;
    }
    return '';
  }, [twelveMonthData]);

  const localActiveIdx = useMemo(() => {
    const visible = currentData.slice(
      windowStart,
      windowStart + PAGE_SIZE
    );
    const current = activeIdx - windowStart;
    if (
      current >= 0 &&
      current < visible.length &&
      visible[current]?.value !== null
    ) {
      return current;
    }
    const firstValid = visible.findIndex(
      item => item.value !== null
    );
    return firstValid;
  }, [activeIdx, currentData, windowStart]);

  const handlePointClick = localIdx => setActiveIdx(windowStart + localIdx);
  const handleHoverPoint = (index, position) => {
    setHoveredIdx(index);
    setTooltipPos(position);
  };

  const activeCat = getPointCategory(currentData[activeIdx]?.value ?? currentValue);
  const isB12 =marker.code === "B12A";
  const legendLabels = buildLegendLabels(ranges,true,isB12);
  const displayPeriodLabel = tab === 'last12' ? twelveMonthPeriodLabel : dynamicPeriodLabel;

  const lineData = useMemo(() => {
  const start = windowStart;
  const end = windowStart + PAGE_SIZE - 1;

  let prevValid = null;

  for (let i = start - 1; i >= 0; i--) {
    if (currentData[i]?.value !== null) {
      prevValid = currentData[i];
      break;
    }
  }

  let nextValid = null;

  for (let i = end + 1; i < currentData.length; i++) {
    if (currentData[i]?.value !== null) {
      nextValid = currentData[i];
      break;
    }
  }

  const visibleSlice = currentData.slice(start, end + 1);


  return [
    ...(prevValid ? [prevValid] : []),
    ...visibleSlice,
    ...(nextValid ? [nextValid] : []),
  ];
}, [currentData, windowStart]);

const hasPreviousData = useMemo(() => {
  for (let i = windowStart - 1; i >= 0; i--) {
    if (currentData[i]?.value !== null) {
      return true;
    }
  }
  return false;
}, [currentData, windowStart]);


  return (
    <div className={`mg ${isMobile && "px-0"}`}>
      {displayPeriodLabel && <div className="mg__period">{displayPeriodLabel}</div>}

      {aggregatedAllResultsData.length > 0 && (
        <div className="mg__tabs">
          <div className="mg__tabs-inner">
            {[
              { key: 'last12', label: 'Last 12 months' },
              { key: 'all', label: 'All results' },
            ].map(t => (
              <button
                key={t.key}
                className={`mg__tab${tab === t.key ? ' mg__tab--active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mg__nav">
        <button className="mg__arrow" onClick={goPrev} disabled={!canGoPrev} title="Previous months">
          <svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.225367 7.54407L6.45596 13.7746C6.75646 14.0751 7.24363 14.0751 7.5441 13.7746L8.27079 13.0479C8.57077 12.748 8.57135 12.2618 8.27207 11.9611L3.33422 6.99998L8.27207 2.03892C8.57135 1.73823 8.57077 1.25205 8.27079 0.952066L7.5441 0.225371C7.2436 -0.0751237 6.75642 -0.0751237 6.45596 0.225371L0.225399 6.45593C-0.0750961 6.75639 -0.0750961 7.24357 0.225367 7.54407Z" fill="#003032"/>
          </svg>
        </button>
        <div className="mg__nav-pill">Tap data points and arrows to explore</div>
        <button className="mg__arrow" onClick={goNext} disabled={!canGoNext} title="Next months">
          <svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.27079 7.54407L2.04019 13.7746C1.7397 14.0751 1.25252 14.0751 0.952059 13.7746L0.225364 13.0479C-0.0746183 12.748 -0.0751952 12.2618 0.224081 11.9611L5.16194 6.99998L0.224081 2.03892C-0.0751952 1.73823 -0.0746183 1.25205 0.225364 0.952066L0.952059 0.225371C1.25255 -0.0751237 1.73973 -0.0751237 2.04019 0.225371L8.27076 6.45593C8.57125 6.75639 8.57125 7.24357 8.27079 7.54407Z" fill="#003032"/>
          </svg>
        </button>
      </div>

      <Tooltip
        data={currentData}
        index={hoveredIdx !== null ? hoveredIdx : activeIdx}
        unit={unit}
        getPointCategory={getPointCategory}
        position={tooltipPos}
        visible={hoveredIdx !== null}
      />

      <div className="mg__chart-card">
        <LineChart
          data={visibleData}
          activeIndex={localActiveIdx}
          onPointClick={handlePointClick}
          activeCat={activeCat}
          yTicks={yTicks}
          yMax={yMax}
          zoneBands={zoneBands}
          xLabels={visibleLabels}
          onHoverPoint={handleHoverPoint}
          hoveredIndex={hoveredIdx}
          fullDataIndices={visibleIndices}
          ignoreGaps={tab === 'last12'}
          fullData={tab === 'last12' ? currentData : null}
          globalData={tab === 'all' ? currentData : null}
          grid={{ vertical: true, horizontal: true }}
          isManual={marker.decision_type?.includes("manual")}
          isMobileView={isMobileView}
          getValuePositionInZone={getValuePositionInZone}
          marker={marker}
          lineData={lineData}
          hasPreviousData={hasPreviousData}
        />
      </div>
      {marker.decision_type?.includes("manual") ? 
        <p className='graph-manual-legend'>Please note risk categorisation has been interpreted by the doctor based on your health and cycle information at the time of review.</p>
       :
        <div className="mg__legend">
          {ranges.map((r, i) => (
            <div key={r.key} className="mg__legend-item">
              <div className="mg__legend-dot" style={{ backgroundColor: r.dotColor }} />
              <span className="mg__legend-label">
                <span className="label-prefix">{legendLabels[i].prefix}</span>
                <span className="label-suffix">{legendLabels[i].suffix}</span>
              </span>
            </div>
          ))}
        </div>
      }
      
    </div>
  );
}